import { CATEGORIES } from "@bl/domain";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Columns3,
  Filter,
  LayoutGrid,
  List,
  Rows3,
  SlidersHorizontal,
} from "lucide-react";
import type { ReactNode } from "react";
import type { Location } from "../api";
import { Menu, MenuDivider, MenuHeading, MenuItem } from "../components/ui";
import { categoryLabel } from "../format";
import {
  type ColumnSetting,
  INVENTORY_COLUMNS,
  type InventoryView,
  type SortKey,
} from "../layout/layout";
import type { Inv } from "./InventoryCards";

export function ViewMenu({
  view,
  onChange,
}: {
  view: InventoryView;
  onChange: (v: InventoryView) => void;
}) {
  const current = VIEWS.find((v) => v.id === view) ?? VIEWS[0];
  return (
    <Menu icon={current?.icon ?? <List size={20} />} label="View">
      {(close) => <ViewChoices view={view} onChange={onChange} close={close} />}
    </Menu>
  );
}
const VIEWS: { id: InventoryView; label: string; icon: ReactNode }[] = [
  { id: "table", label: "Table", icon: <List size={16} /> },
  { id: "cards", label: "Cards", icon: <LayoutGrid size={16} /> },
  { id: "overview", label: "Overview", icon: <Rows3 size={16} /> },
];
function ViewChoices({
  view,
  onChange,
  close,
}: {
  view: InventoryView;
  onChange: (v: InventoryView) => void;
  close: () => void;
}) {
  return VIEWS.map((v) => (
    <MenuItem
      key={v.id}
      checked={v.id === view}
      onClick={() => {
        onChange(v.id);
        close();
      }}
    >
      {v.label}
    </MenuItem>
  ));
}
export function SortMenu({
  sort,
  onChange,
}: {
  sort: Inv["sort"];
  onChange: (s: Inv["sort"]) => void;
}) {
  return (
    <Menu icon={<ArrowUpDown size={20} />} label="Sort">
      {() => <SortChoices sort={sort} onChange={onChange} />}
    </Menu>
  );
}
function SortChoices({
  sort,
  onChange,
}: {
  sort: Inv["sort"];
  onChange: (s: Inv["sort"]) => void;
}) {
  const keys: { id: SortKey; label: string }[] = [
    { id: "name", label: "Name" },
    { id: "category", label: "Category" },
    { id: "onHand", label: "On hand" },
    { id: "lastMovement", label: "Last change" },
  ];
  return (
    <>
      <MenuHeading>Sort by</MenuHeading>
      {keys.map((k) => (
        <MenuItem
          key={k.id}
          checked={sort.key === k.id}
          onClick={() =>
            onChange({
              key: k.id,
              dir: sort.key === k.id && sort.dir === "asc" ? "desc" : "asc",
            })
          }
        >
          {k.label}
          {sort.key === k.id ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
        </MenuItem>
      ))}
    </>
  );
}
export function FilterMenu({
  inv,
  locations,
  active,
  onChange,
}: {
  inv: Inv;
  locations: Location[];
  active: boolean;
  onChange: (patch: Partial<Inv>) => void;
}) {
  return (
    <Menu icon={<Filter size={20} />} label="Filter" badge={active}>
      {() => <FilterChoices inv={inv} locations={locations} onChange={onChange} />}
    </Menu>
  );
}
function FilterChoices({
  inv,
  locations,
  onChange,
}: {
  inv: Inv;
  locations: Location[];
  onChange: (patch: Partial<Inv>) => void;
}) {
  return (
    <>
      <MenuHeading>Category</MenuHeading>
      <MenuItem checked={inv.category === null} onClick={() => onChange({ category: null })}>
        All
      </MenuItem>
      {CATEGORIES.map((c) => (
        <MenuItem key={c} checked={inv.category === c} onClick={() => onChange({ category: c })}>
          {categoryLabel(c)}
        </MenuItem>
      ))}
      <MenuDivider />
      <MenuHeading>Location</MenuHeading>
      <MenuItem checked={inv.locationId === null} onClick={() => onChange({ locationId: null })}>
        Everywhere
      </MenuItem>
      {locations
        .filter((l) => !l.archived)
        .map((l) => (
          <MenuItem
            key={l.id}
            checked={inv.locationId === l.id}
            onClick={() => onChange({ locationId: l.id })}
          >
            {l.name}
          </MenuItem>
        ))}
      <MenuDivider />
      <MenuItem checked={inv.hideEmpty} onClick={() => onChange({ hideEmpty: !inv.hideEmpty })}>
        Hide items with none on hand
      </MenuItem>
    </>
  );
}
export function OptionsMenu({
  inv,
  onChange,
}: {
  inv: Inv;
  onChange: (patch: Partial<Inv>) => void;
}) {
  return (
    <Menu icon={<Columns3 size={20} />} label="Options">
      {() => <OptionChoices inv={inv} onChange={onChange} />}
    </Menu>
  );
}
function OptionChoices({ inv, onChange }: { inv: Inv; onChange: (patch: Partial<Inv>) => void }) {
  return (
    <>
      <MenuItem
        checked={inv.groupByCategory}
        onClick={() => onChange({ groupByCategory: !inv.groupByCategory })}
      >
        Group by category
      </MenuItem>
      {inv.view === "cards" ? (
        <>
          <MenuDivider />
          <MenuHeading>Card size</MenuHeading>
          {(["small", "medium", "large"] as const).map((s) => (
            <MenuItem
              key={s}
              checked={inv.cardSize === s}
              onClick={() => onChange({ cardSize: s })}
            >
              {s[0]?.toUpperCase()}
              {s.slice(1)}
            </MenuItem>
          ))}
        </>
      ) : null}
      {inv.view === "table" ? (
        <ColumnOptions
          columns={inv.columns}
          defs={INVENTORY_COLUMNS}
          onChange={(columns) => onChange({ columns })}
        />
      ) : null}
    </>
  );
}
/** A table's columns in its Options menu: each shown or hidden, and moved up or down. */
export function ColumnOptions<Id extends string>({
  columns,
  defs,
  onChange,
}: {
  columns: ColumnSetting<Id>[];
  defs: readonly { id: Id; label: string; required?: boolean }[];
  onChange: (columns: ColumnSetting<Id>[]) => void;
}) {
  const move = (index: number, by: -1 | 1) => {
    const next = [...columns];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [
      next[target] as ColumnSetting<Id>,
      next[index] as ColumnSetting<Id>,
    ];
    onChange(next);
  };
  return (
    <>
      <MenuDivider />
      <MenuHeading>Columns</MenuHeading>
      {columns.map((c, index) => {
        const def = defs.find((d) => d.id === c.id);
        return (
          <div key={c.id} className="column-option">
            <MenuItem
              checked={c.visible}
              disabled={Boolean(def?.required)}
              onClick={() =>
                onChange(columns.map((x) => (x.id === c.id ? { ...x, visible: !x.visible } : x)))
              }
            >
              {def?.label}
            </MenuItem>
            <button
              type="button"
              className="icon-button"
              aria-label={`Move ${def?.label} up`}
              onClick={() => move(index, -1)}
            >
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              className="icon-button"
              aria-label={`Move ${def?.label} down`}
              onClick={() => move(index, 1)}
            >
              <ArrowDown size={14} />
            </button>
          </div>
        );
      })}
    </>
  );
}
/**
 * On a phone, every way of viewing Inventory under one button, so the toolbar has room to name the
 * floor's verbs (the re-critique, 2026-10-06: nine unlabeled glyphs on the brewer's own device).
 */
export function PhoneViewMenu({
  inv,
  locations,
  active,
  onChange,
}: {
  inv: Inv;
  locations: Location[];
  active: boolean;
  onChange: (patch: Partial<Inv>) => void;
}) {
  return (
    <Menu icon={<SlidersHorizontal size={20} />} label="View" badge={active} labeled>
      {(close) => (
        <>
          <MenuItem
            checked={inv.quantityMode === "pack"}
            onClick={() =>
              onChange({ quantityMode: inv.quantityMode === "pack" ? "unit" : "pack" })
            }
          >
            Show amounts in packs
          </MenuItem>
          <MenuDivider />
          <MenuHeading>View</MenuHeading>
          <ViewChoices view={inv.view} onChange={(view) => onChange({ view })} close={close} />
          <MenuDivider />
          <SortChoices sort={inv.sort} onChange={(sort) => onChange({ sort })} />
          <MenuDivider />
          <FilterChoices inv={inv} locations={locations} onChange={onChange} />
          <MenuDivider />
          <OptionChoices inv={inv} onChange={onChange} />
        </>
      )}
    </Menu>
  );
}
